import { msg } from "@lingui/core/macro";
import {
  addPurchaseLineMutation,
  createPurchaseOrderMutation,
  createSupplierMutation,
  formatMoney,
  getIngredientsQuery,
  getPurchaseOrderLinesQuery,
  getPurchaseOrdersQuery,
  getSuppliersQuery,
  parseMoneyToCents,
  queryKeys,
  receivePurchaseOrderMutation,
} from "@comandero/shared";
import type {
  Branch,
  Ingredient,
  Organization,
  PurchaseOrder,
  PurchaseOrderLine,
  Supplier,
} from "@comandero/powersync";
import { createPowerSyncQuery } from "@comandero/shared/solid";
import { createEffect, createSignal, For, Show, untrack } from "solid-js";
import { Button } from "@/components/ui/button";
import { Select, toSelectOptions } from "@/components/ui/select";
import { TextField } from "@/components/ui/text-field";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";

interface PurchasingManagerProps {
  org: () => Organization | null;
  branch: () => Branch | null;
  /** PO seleccionada desde la URL (`?po=`). Hace el detalle addressable (#15b). */
  poParam?: () => string | undefined;
  /** Escribe la PO seleccionada en la URL (`replace`). */
  onPoChange?: (po: string | null) => void;
}

export function PurchasingManager(props: PurchasingManagerProps) {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const { db } = usePowerSyncContext();

  const [supplierName, setSupplierName] = createSignal("");
  const [activePo, setActivePo] = createSignal<string | null>(null);
  const [lineIngredient, setLineIngredient] = createSignal("");

  // URL → signal: la PO de la URL (`?po=`) manda. Copiar/pegar la URL reabre el
  // mismo detalle; sin param, no hay PO activa. Idempotente.
  // CC-16: la comparación lee `activePo()` en `untrack` — si el efecto trackeara
  // la señal que él mismo escribe, un `selectPo()` del usuario lo re-dispararía
  // con la URL aún vieja y revertiría la selección.
  createEffect(() => {
    const fromUrl = props.poParam?.() ?? null;
    if (fromUrl !== untrack(activePo)) {
      setActivePo(fromUrl);
    }
  });

  // signal → URL: centraliza la selección para mantener URL y signal en sync.
  const selectPo = (po: string | null) => {
    setActivePo(po);
    props.onPoChange?.(po);
  };
  const [lineQty, setLineQty] = createSignal("");
  const [lineCost, setLineCost] = createSignal("");

  const orgId = (): string | null => props.org()?.id ?? null;

  const suppliers = createPowerSyncQuery<Supplier>({
    queryKey: () => queryKeys.comandero.suppliers(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getSuppliersQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  const ingredients = createPowerSyncQuery<Ingredient>({
    queryKey: () => queryKeys.comandero.ingredients(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getIngredientsQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  const purchaseOrders = createPowerSyncQuery<PurchaseOrder>({
    queryKey: () => queryKeys.comandero.purchaseOrders(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getPurchaseOrdersQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  const poLines = createPowerSyncQuery<PurchaseOrderLine>({
    queryKey: () => queryKeys.comandero.purchaseLines(activePo() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const po = activePo();
      return d && po ? getPurchaseOrderLinesQuery(d, po) : null;
    },
    db,
    enabled: () => Boolean(activePo()),
  });

  const ingredientName = (id: string | null | undefined): string =>
    ingredients.data?.find((i) => i.id === id)?.name ?? "—";

  async function addSupplier(e: Event) {
    e.preventDefault();
    const d = drizzle();
    const id = orgId();
    if (!d || !id || !supplierName().trim()) {
      return;
    }
    await createSupplierMutation(d, id, supplierName());
    setSupplierName("");
  }

  async function newPurchaseOrder() {
    const d = drizzle();
    const id = orgId();
    if (!d || !id) {
      return;
    }
    const poId = await createPurchaseOrderMutation(d, id, {
      branchId: props.branch()?.id ?? null,
      supplierId: suppliers.data?.[0]?.id ?? null,
    });
    selectPo(poId);
  }

  async function addLine(e: Event) {
    e.preventDefault();
    const d = drizzle();
    const id = orgId();
    const po = activePo();
    const ing = lineIngredient();
    const qty = Number.parseInt(lineQty() || "0", 10);
    if (!d || !id || !po || !ing || !Number.isFinite(qty) || qty <= 0) {
      return;
    }
    await addPurchaseLineMutation(d, id, po, ing, qty, parseMoneyToCents(lineCost()));
    setLineQty("");
    setLineCost("");
  }

  async function receive(poId: string) {
    const d = drizzle();
    if (!d) {
      return;
    }
    await receivePurchaseOrderMutation(d, poId);
  }

  return (
    <div class="flex flex-col gap-8">
      {/* Suppliers */}
      <section>
        <h2 class="mb-3 text-lg font-semibold">{t(msg`Suppliers`)}</h2>
        <form class="mb-3 flex gap-2" onSubmit={addSupplier}>
          <TextField
            data-testid="supplier-name-input"
            placeholder={t(msg`Supplier name`)}
            value={supplierName()}
            onInput={(e) => setSupplierName(e.currentTarget.value)}
          />
          <Button data-testid="add-supplier-btn" type="submit">
            {t(msg`Add supplier`)}
          </Button>
        </form>
        <ul class="flex flex-wrap gap-2">
          <For each={suppliers.data}>
            {(s) => (
              <li
                class="rounded-md border border-border/40 px-3 py-1 text-sm"
                data-testid="supplier-item"
              >
                {s.name}
              </li>
            )}
          </For>
        </ul>
      </section>

      {/* Purchase order builder */}
      <section>
        <div class="mb-3 flex items-center justify-between">
          <h2 class="text-lg font-semibold">{t(msg`Purchase orders`)}</h2>
          <Button data-testid="new-po-btn" onClick={() => void newPurchaseOrder()}>
            {t(msg`New purchase order`)}
          </Button>
        </div>

        <Show when={activePo()}>
          <div
            class="mb-4 rounded-md border border-border/40 p-3"
            data-testid="po-builder"
            data-po-id={activePo() ?? ""}
          >
            <form class="mb-3 flex flex-wrap gap-2" onSubmit={addLine}>
              <Select
                data-testid="po-ingredient-select"
                aria-label={t(msg`Ingredient`)}
                placeholder={t(msg`Select ingredient`)}
                value={lineIngredient()}
                onChange={setLineIngredient}
                options={toSelectOptions(
                  ingredients.data,
                  (i) => i.id,
                  (i) => i.name,
                )}
              />
              <TextField
                data-testid="po-qty-input"
                placeholder={t(msg`Qty`)}
                inputmode="numeric"
                value={lineQty()}
                onInput={(e) => setLineQty(e.currentTarget.value)}
              />
              <TextField
                data-testid="po-cost-input"
                placeholder={t(msg`Unit cost`)}
                inputmode="decimal"
                value={lineCost()}
                onInput={(e) => setLineCost(e.currentTarget.value)}
              />
              <Button data-testid="add-po-line-btn" type="submit">
                {t(msg`Add line`)}
              </Button>
            </form>
            <ul class="flex flex-col gap-1" data-testid="po-lines">
              <For each={poLines.data}>
                {(l) => (
                  <li class="flex justify-between text-sm" data-testid="po-line-row">
                    <span>
                      {l.qty}× {ingredientName(l.ingredient_id)}
                    </span>
                    <span class="text-muted-foreground">{formatMoney(l.line_total)}</span>
                  </li>
                )}
              </For>
            </ul>
          </div>
        </Show>

        <ul class="flex flex-col gap-2">
          <For each={purchaseOrders.data}>
            {(po) => (
              <li
                class="flex items-center justify-between gap-3 rounded-md border border-border/40 px-3 py-2 text-sm"
                data-testid="po-item"
                data-po-id={po.id}
                data-po-status={po.status}
              >
                <button
                  type="button"
                  class="text-left hover:underline"
                  data-testid="po-select-btn"
                  onClick={() => selectPo(po.id)}
                >
                  {formatMoney(po.total)}
                </button>
                <span class="text-muted-foreground" data-testid="po-row-status">
                  {po.status}
                </span>
                <Show when={po.status !== "received"}>
                  <Button
                    variant="secondary"
                    size="sm"
                    data-testid="receive-po-btn"
                    onClick={() => void receive(po.id)}
                  >
                    {t(msg`Receive`)}
                  </Button>
                </Show>
                <Show when={po.status === "received"}>
                  <span
                    class="rounded bg-green-500/10 px-2 py-0.5 text-xs text-green-600"
                    data-testid="po-received-badge"
                  >
                    {t(msg`Received`)}
                  </span>
                </Show>
              </li>
            )}
          </For>
        </ul>
      </section>
    </div>
  );
}
